import React from 'react';
import { format } from 'date-fns';
import { LeadDetailsResponse, LeadStateResponse } from '../types';
import Section from '@hero/ui-kit/layout/Section';
import Pill from '../components/Pill';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import P from '@hero/ui-kit/typography/P';
import Divider from '@hero/ui-kit/components/Divider';
import * as Style from './style.module.scss';
import PlusIcon from '@hero/ui-kit/icons/utility/PlusIcon';
import VerifiedIcon from '@hero/ui-kit/icons/utility/VerifiedIcon';
import CalendarIcon from '@hero/ui-kit/icons/utility/CalendarIcon';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import PatientDetailsForm from '../components/PatientDetailsForm';
import ContactPersonForm from '../components/ContactPersonForm';
import ShippingAddressForm from '../components/ShippingAddressForm';
import getInsuranceTypeLabel from '../utils/getInsuranceTypeLabel';
import { formatDateTimeLong } from '../utils/formatDateTime';
import { browserTimezone, zoneLabel } from '../components/modals/SlotPicker/timeFormat';
import { getMedicalCheckScheduledAt, getMedicalCheckStatus } from '../utils/medicalCheckStatus';
import Button from '@hero/ui-kit/inputs/Button';
import ConsentPDF from './ConsentPDF';
import { getDOBFormat } from '../../../utils/date';
import InsuranceImage from './InsuranceImage';
import useValidateLeadAddress, { ValidateAddressFieldErrors } from '../api/useValidateLeadAddress';
import { useOptionalLeadDetailsContext } from './v2/context/LeadDetailsContext';

// HWM-22633 round 3: backend `address_validator.py` returns 400 with
// `data: { <field>: [<code>] }`. Map each `<field>.<code>` to the
// human-readable text agents see in the inline error block. Anything not in
// this map falls back to `<field>: <code>` so a new BE code surfaces visibly
// instead of silently going missing.
const ADDRESS_FIELD_ERROR_TEXT: Record<string, string> = {
    // ZIP — structural and overflow
    'zip.invalid_format': 'ZIP must be 5 digits (or ZIP+4).',
    'zip.too_long': 'ZIP is too long.',
    // State — accepts either 2-letter code ("NY") or full name ("New York").
    'state.invalid_state': 'State must be a US state code or name.',
    'state.state_zip_mismatch': "State doesn't match the ZIP code's state.",
    // Street address — required + length bound
    'street_address.required': 'Street address is required.',
    'street_address.too_long': 'Street address is too long (max 255).',
    // Apartment (line 2) — length bound only
    'apartment.too_long': 'Apartment / suite is too long (max 128).',
    // City — required + length bound
    'city.required': 'City is required.',
    'city.too_long': 'City is too long (max 128).'
};
import CommunicationModal from '../components/modals/CommunicationModal';
import CallDispositionModal from '../components/modals/CallDispositionModal';
import PostponeSlaModal from '../components/modals/PostponeSlaModal';
import MedicalCheckSchedulingModal from '../components/modals/MedicalCheckSchedulingModal';
// import AttachmentSection from './AttachmentSection';

interface LeadDetailsProps {
    lead?: LeadDetailsResponse;
    approvalStatus: { label: string; className: string };
    refetchLeadDetails?: () => void;
    refetchLeadState?: () => void;
    isLocked?: boolean;
    onEnrollBtn?: () => void;
    hideEdit?: boolean;
    hideNameSection?: boolean;
    // HWM-22633: when true, render the four CRM action cards (Medical
    // check / Communication / Call Disposition / SLA Timer) inline between
    // Applicant details and Shipping address per Figma 5274:4285. Only the
    // v2 Activities tab passes this; legacy v1 keeps the slim sidebar.
    showCrmActions?: boolean;
    leadState?: LeadStateResponse;
    leadId?: string;
    canEdit?: boolean;
}

// HWM-22633: compact action card used in the LeadContactDetails left rail.
interface ActionCardProps {
    title: string;
    statusText?: string | null;
    statusTone?: 'neutral' | 'warning' | 'danger';
    buttonLabel: string;
    onButtonClick: () => void;
    disabled?: boolean;
    isPrimary?: boolean;
    // [HWM-22633 round 4e] Hide the action button entirely (vs.
    // disabling it). Used by the Medical Check card once the lead is
    // past MEDICAL_CHECK status — scheduling / rescheduling has no
    // meaning at that point and a disabled button would just be
    // visual noise.
    hideButton?: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({
    title,
    statusText,
    statusTone = 'neutral',
    buttonLabel,
    onButtonClick,
    disabled,
    isPrimary,
    hideButton = false
}) => {
    const toneClass =
        statusTone === 'danger' ? Style.danger : statusTone === 'warning' ? Style.warning : '';
    return (
        <div className={Style.actionCard}>
            <div className={Style.actionCardHeader}>
                <P noDefaultMargin strong size="large">
                    {title}
                </P>
                {statusText ? (
                    <P
                        size="small"
                        noDefaultMargin
                        className={`${Style.actionCardStatus} ${toneClass}`}
                    >
                        {statusText}
                    </P>
                ) : null}
            </div>
            {hideButton ? null : (
                <Button
                    variant={isPrimary ? 'primary' : 'outlineSecondary'}
                    onClick={onButtonClick}
                    disabled={disabled}
                    className={Style.actionCardBtn}
                >
                    {buttonLabel}
                </Button>
            )}
        </div>
    );
};

const formatSlaStatus = (
    lead?: LeadDetailsResponse
): { text: string; tone: 'neutral' | 'warning' | 'danger' } => {
    if (lead?.sla_text) {
        const tone = lead.sla_text.toLowerCase().includes('overdue')
            ? 'danger'
            : lead.sla_text.includes('≤20') || lead.sla_text.toLowerCase().includes('20 min')
              ? 'warning'
              : 'neutral';
        return { text: lead.sla_text, tone };
    }
    if (lead?.sla_postponed_until) {
        return {
            text: `Postponed until ${format(new Date(lead.sla_postponed_until), 'MMM d, yyyy')}`,
            tone: 'neutral'
        };
    }
    return { text: '', tone: 'neutral' };
};

const formatMedicalCheckStatus = (lead?: LeadDetailsResponse): string => {
    // HWM-22633: derive MC stage from approval_status / approval_sub_status
    // via the shared util (BE column was deleted in round 5).
    const mcStatus = getMedicalCheckStatus(lead);
    if (mcStatus === 'missed') return 'Missed';
    if (mcStatus === 'completed') return 'Completed';
    // For an active schedule, show the actual slot from
    // `status_change_note_date_time` (where both DrChrono booking and the
    // manual flow land the appointment time).
    const scheduledAt = getMedicalCheckScheduledAt(lead);
    if (scheduledAt) {
        const scheduledDate = new Date(scheduledAt);
        if (!Number.isNaN(scheduledDate.getTime())) {
            // HWM-23070: the stamp is a true UTC instant, so this renders in
            // the agent's own zone — name it, or a care manager outside the
            // clinic's timezone can't tell which clock they're reading.
            const zone = zoneLabel(scheduledAt, browserTimezone());
            return `Scheduled ${formatDateTimeLong(scheduledAt)} ${zone}`;
        }
    }
    if (mcStatus === 'scheduled') return 'Scheduled';
    return 'Not scheduled';
};

const getLabel = (label?: string | null) => {
    return label || '-';
};

const LeadContactDetails: React.FC<LeadDetailsProps> = ({
    lead,
    approvalStatus,
    refetchLeadDetails,
    refetchLeadState,
    isLocked = false,
    hideEdit = false,
    hideNameSection = false,
    onEnrollBtn,
    showCrmActions = false,
    leadId,
    canEdit = true
}) => {
    const [showPatientDetailsModal, setShowPatientDetailsModal] = React.useState(false);
    const [showContactDetailsModal, setShowContactDetailsModal] = React.useState(false);
    const [showShippingAddressModal, setShowShippingAddressModal] = React.useState(false);

    // HWM-22633: CRM action card modal state (moved here from LeadActivities
    // so the cards live in the left rail per Figma 5274:4285).
    const [showCommunicationModal, setShowCommunicationModal] = React.useState(false);
    const [showCallDispositionModal, setShowCallDispositionModal] = React.useState(false);
    const [showPostponeSlaModal, setShowPostponeSlaModal] = React.useState(false);
    // HWM-22633 / HWM-22859: medical-check scheduling modal. A single flag —
    // `MedicalCheckSchedulingModal` picks the provider-appropriate UI
    // (DrChrono slot picker vs manual date/time + note), so this component no
    // longer needs to know which one to render. Replaces the old cross-tab
    // scrollIntoView hack into the Demographics-only widget.
    const [showMcModal, setShowMcModal] = React.useState(false);
    // [HWM-22633 round 4d] Tracks whether the next open of the DrChrono
    // schedule modal should run as a reschedule (cancel+book chain) vs
    // a fresh booking. Set from `handleMedicalCheckClick` based on
    // whether the lead already has `medical_check_scheduled_at` set.
    const [isReschedulingMc, setIsReschedulingMc] = React.useState(false);

    // HWM-22633: Validate-existing-address button. Click POSTs to a dedicated
    // backend endpoint that runs structural validation on the lead's
    // already-saved shipping address (zip regex, 2-letter state code, non-empty
    // street/city). When valid the backend flips `address_validation_status`
    // to `validated`. No new address fields are sent — uses what's on file.
    // Round 3: 400 responses carry a `data` map of field → error codes. We
    // surface those inline so the agent sees what's wrong, and we offer an
    // "Edit address" link that scrolls to the Demographics-tab address form.
    const [addressErrors, setAddressErrors] = React.useState<ValidateAddressFieldErrors | null>(
        null
    );
    const { mutate: validateAddress, isPending: isValidating } = useValidateLeadAddress({
        onSuccess: () => setAddressErrors(null),
        onValidationError: (fields) => setAddressErrors(fields)
    });
    const isAddressValidated = lead?.address_validation_status === 'validated';

    const handleValidateAddress = () => {
        const targetLeadId = leadId || lead?.primary_user_details?.lead_id;
        if (!targetLeadId) return;
        setAddressErrors(null);
        validateAddress({ leadId: targetLeadId });
    };

    // Round 3: "Edit address" affordance — switches to the Demographics
    // (`general`) tab if we're not already there, then scrolls to the
    // shipping section anchor (`id="shipping"` on the General tab form).
    // The context is optional because this component also renders on the v1
    // legacy detail page (no provider) — there we fall through to a plain
    // scrollIntoView which is a no-op if the form isn't mounted.
    const leadDetailsCtx = useOptionalLeadDetailsContext();
    const handleEditAddress = React.useCallback(() => {
        if (leadDetailsCtx && leadDetailsCtx.tab !== 'general') {
            leadDetailsCtx.handleChangeTab('general');
        }
        // Defer scroll one tick so the General tab has mounted the form.
        requestAnimationFrame(() => {
            document
                .getElementById('shipping')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [leadDetailsCtx]);

    // HWM-22633: handlers for the CRM cards. Mutations invalidate
    // LEAD_DETAILS on success so we deliberately do NOT call refetchLeadDetails
    // here — that would double-fetch on save and waste a fetch on cancel.
    const handleCloseCrmModal = () => {
        setShowCommunicationModal(false);
        setShowCallDispositionModal(false);
        setShowPostponeSlaModal(false);
    };

    const handleCloseMcModal = () => {
        setShowMcModal(false);
        setIsReschedulingMc(false);
    };

    const handleMcModalSuccess = () => {
        setShowMcModal(false);
        refetchLeadDetails && refetchLeadDetails();
        refetchLeadState && refetchLeadState();
    };

    const handleMedicalCheckClick = () => {
        // HWM-22633 / HWM-22859: open the medical-check scheduling modal. The
        // `MedicalCheckSchedulingModal` wrapper picks the provider-appropriate
        // UI (DrChrono slot picker vs manual date/time + note), so this handler
        // only decides reschedule mode.
        //
        // [round 4d / V4] When the lead already has an active (or missed)
        // schedule, open in reschedule mode. For DrChrono this drives the
        // cancel-then-book chain past the `_check_existing_appointment` guard;
        // for the manual modal it only changes the heading. `getMedicalCheck
        // ScheduledAt` returns null for a missed lead, so key off the derived
        // medical-check status instead.
        const mcStatus = getMedicalCheckStatus(lead);
        const hasExistingAppointment = mcStatus === 'scheduled' || mcStatus === 'missed';
        setIsReschedulingMc(hasExistingAppointment);
        setShowMcModal(true);
    };

    const messagingEnabled = lead?.messaging_enabled ?? false;
    const slaStatus = formatSlaStatus(lead);
    const medicalCheckText = formatMedicalCheckStatus(lead);
    const patientName = `${lead?.primary_user_details?.first_name || ''} ${
        lead?.primary_user_details?.last_name || ''
    }`.trim();
    const crmDisabled = !canEdit || isLocked;
    // HWM-22832: EXPIRED / REJECTED are terminal — scheduling a medical check
    // would attempt an invalid reactivation transition (backend raises
    // LeadStateMachineException: already_reactivated_once, surfaced as a
    // "Something went wrong" toast). Disable the Medical Check action so the
    // agent can't trigger that error.
    const isTerminalStatus =
        lead?.status?.toUpperCase() === 'EXPIRED' || lead?.status?.toUpperCase() === 'REJECTED';
    const effectiveLeadId = leadId || lead?.primary_user_details?.lead_id || '';
    const disableEdit =
        (lead?.status?.toUpperCase() === 'APPROVED' &&
            lead?.sub_status?.toUpperCase() === 'ENROLLED') ||
        isLocked ||
        lead?.status?.toUpperCase() === 'REJECTED';

    const handleOpenPatientDetailsForm = () => {
        setShowPatientDetailsModal(true);
    };
    const handleClosePatientDetailsForm = () => {
        setShowPatientDetailsModal(false);
    };

    const handleOpenContactDetailsForm = () => {
        setShowContactDetailsModal(true);
    };
    const handleCloseContactDetailsForm = () => {
        setShowContactDetailsModal(false);
    };

    const handleOpenShippingAddressForm = () => {
        setShowShippingAddressModal(true);
    };
    const handleCloseShippingAddressForm = () => {
        setShowShippingAddressModal(false);
    };

    const handleShippingAddressSuccess = () => {
        handleCloseShippingAddressForm();
        refetchLeadDetails && refetchLeadDetails();
    };

    const handleContactDetailsSuccess = () => {
        handleCloseContactDetailsForm();
        refetchLeadDetails && refetchLeadDetails();
    };

    const handlePatientDetailsSuccess = () => {
        handleClosePatientDetailsForm();
        refetchLeadDetails && refetchLeadDetails();
    };

    const showEdit = !disableEdit && !hideEdit;

    return (
        <>
            <Section border noDefaultPadding styles={{ minWidth: '45rem' }}>
                {!hideNameSection ? (
                    <>
                        <Section padding="small">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Pill
                                    label={approvalStatus.label}
                                    className={approvalStatus.className}
                                />
                                <Button
                                    onClick={onEnrollBtn}
                                    size="small"
                                    styles={{ visibility: 'hidden' }} // TODO enrollment
                                >
                                    Enroll
                                </Button>
                            </div>
                            <Section
                                paddingHorizontal="none"
                                styles={{ paddingTop: '1.6rem', paddingBottom: '1.2rem' }}
                            >
                                <P
                                    strong
                                    size="large"
                                    noDefaultMargin
                                    styles={{ fontSize: '2.4rem' }}
                                >
                                    {lead?.primary_user_details?.first_name &&
                                    lead?.primary_user_details?.last_name
                                        ? `${lead.primary_user_details.first_name} ${lead.primary_user_details.last_name}`
                                        : `-`}
                                </P>
                            </Section>

                            <P
                                noDefaultMargin
                                styles={{
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    marginBottom: '2.4rem'
                                }}
                                title={getLabel(lead?.primary_user_details?.lead_id)}
                            >{`Form fill ID: ${getLabel(lead?.primary_user_details?.lead_id)}`}</P>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    columnGap: '1rem',
                                    marginBottom: lead?.is_existing_member ? '1.2rem' : 0
                                }}
                            >
                                <CalendarIcon type="utility" width={20} height={20} />
                                <P noDefaultMargin size="small">
                                    {getLabel(
                                        lead?.primary_user_details?.completed_date ||
                                            lead?.primary_user_details?.created_date
                                    )}
                                </P>
                            </div>

                            {lead?.is_existing_member ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: '1rem'
                                    }}
                                >
                                    <VerifiedIcon type="utility" width={20} height={20} />
                                    <div>
                                        <P noDefaultMargin size="small" strong>
                                            Using Hero dispenser
                                        </P>
                                        {lead?.is_forbidden_rtm_election ? (
                                            <P
                                                noDefaultMargin
                                                size="small"
                                                styles={{ color: '#e83f3f', fontWeight: 400 }}
                                            >
                                                Already enrolled with another provider!
                                            </P>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </Section>
                        <Divider noDefaultMargin />
                    </>
                ) : null}
                <Section padding="small">
                    <div className={Style.sectionHeader}>
                        <P strong noDefaultMargin size="large">
                            Applicant details
                        </P>
                        {showEdit ? (
                            <ClickableOpacity
                                onClick={handleOpenPatientDetailsForm}
                                alt="Open applicant details form"
                                className={Style.editBtn}
                            >
                                <P strong noDefaultMargin className={Style.editText}>
                                    Edit
                                </P>
                            </ClickableOpacity>
                        ) : null}
                    </div>

                    <P noDefaultMargin size="small" className={Style.label}>
                        DOB
                    </P>
                    <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                        {getDOBFormat(lead?.primary_user_details?.date_of_birth, 'MMM d, yyyy')}
                    </P>
                    <P noDefaultMargin size="small" className={Style.label}>
                        Zip
                    </P>
                    <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                        {getLabel(lead?.primary_user_details.zip)}
                    </P>
                    <P noDefaultMargin size="small" className={Style.label}>
                        Email
                    </P>
                    <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                        {getLabel(lead?.primary_user_details?.email)}
                    </P>
                    <P noDefaultMargin size="small" className={Style.label}>
                        Phone number
                    </P>
                    <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                        {getLabel(lead?.primary_user_details?.phone)}
                    </P>
                    <P noDefaultMargin size="small" className={Style.label}>
                        Insurance type
                    </P>
                    <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                        {getInsuranceTypeLabel(lead?.primary_user_details?.insurance_type)}
                    </P>
                    {lead?.primary_user_details?.insurance_type?.toUpperCase() !==
                    'ORIGINAL_MEDICARE' ? (
                        <>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Insurance company
                            </P>
                            <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                                {getLabel(lead?.primary_user_details?.insurance_company)}
                            </P>
                        </>
                    ) : null}

                    <P noDefaultMargin size="small" className={Style.label}>
                        Member ID
                    </P>
                    <P noDefaultMargin>{getLabel(lead?.primary_user_details.insurance_id)}</P>
                    <InsuranceImage lead={lead} />
                </Section>

                {/* HWM-22633: CRM action cards (Medical check / Communication /
                    Call Disposition / SLA Timer) — rendered inline in the left
                    rail between Applicant details and Shipping address per Figma
                    5274:4285. Only shown when showCrmActions is true (v2
                    Activities tab); the legacy v1 sidebar keeps the slim view. */}
                {showCrmActions ? (
                    <>
                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <ActionCard
                                title="Medical check"
                                statusText={medicalCheckText}
                                // HWM-22633: flip the label when an active
                                // schedule exists so the agent knows the click
                                // will reschedule, not double-book.
                                buttonLabel={
                                    getMedicalCheckScheduledAt(lead)
                                        ? 'Reschedule'
                                        : 'Schedule medical check'
                                }
                                onButtonClick={handleMedicalCheckClick}
                                disabled={crmDisabled || isTerminalStatus}
                                isPrimary
                                // HWM-22633: hide the button once the lead is
                                // past MEDICAL_CHECK (subtitle reads
                                // "Completed" via formatMedicalCheckStatus).
                                hideButton={getMedicalCheckStatus(lead) === 'completed'}
                            />
                        </Section>

                        {/* HWM-22633: Communication card is HIDDEN (not just
                                disabled) when the lead's provider doesn't have
                                messaging enabled. */}
                        {messagingEnabled ? (
                            <>
                                <Divider noDefaultMargin />
                                <Section padding="small">
                                    <ActionCard
                                        title="Communication"
                                        statusText="SMS · Email"
                                        buttonLabel="Send SMS/email"
                                        onButtonClick={() => setShowCommunicationModal(true)}
                                        disabled={crmDisabled}
                                    />
                                </Section>
                            </>
                        ) : null}
                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <ActionCard
                                title="Call Attempt &&nbsp;Disposition"
                                buttonLabel="Log call disposition"
                                onButtonClick={() => setShowCallDispositionModal(true)}
                                disabled={crmDisabled}
                            />
                        </Section>

                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <ActionCard
                                title="SLA Timer"
                                statusText={slaStatus.text || 'On track'}
                                statusTone={slaStatus.tone}
                                buttonLabel="Postpone SLA"
                                onButtonClick={() => setShowPostponeSlaModal(true)}
                                disabled={crmDisabled}
                            />
                        </Section>
                    </>
                ) : null}
                {lead?.contact_details?.email ? (
                    <>
                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <div className={Style.sectionHeader}>
                                <P strong noDefaultMargin size="large">
                                    Contact person
                                </P>
                                {showEdit ? (
                                    <ClickableOpacity
                                        onClick={handleOpenContactDetailsForm}
                                        alt="Open contact details form"
                                        className={Style.editBtn}
                                    >
                                        <P strong noDefaultMargin className={Style.editText}>
                                            Edit
                                        </P>
                                    </ClickableOpacity>
                                ) : null}
                            </div>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Name
                            </P>
                            <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                                {lead?.contact_details?.first_name && lead.contact_details.last_name
                                    ? `${lead?.contact_details.first_name} ${lead?.contact_details?.last_name}`
                                    : '-'}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Email
                            </P>
                            <P
                                styles={{
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    marginBottom: '1.2rem'
                                }}
                                title={getLabel(lead?.contact_details?.email)}
                            >
                                {getLabel(lead?.contact_details?.email)}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Phone
                            </P>
                            <P noDefaultMargin>{getLabel(lead?.contact_details?.phone)}</P>
                        </Section>
                    </>
                ) : (
                    <div
                        className={Style.addWrapper}
                        style={{ ...(!showEdit && { display: 'none' }) }}
                    >
                        <Divider noDefaultMargin />
                        <ClickableOpacity
                            onClick={handleOpenContactDetailsForm}
                            alt="Open contact details form"
                            className={Style.addFooter}
                            disabled={!showEdit}
                        >
                            <PlusIcon size="utility" width={20} height={20} type="brand" />
                            <P strong noDefaultMargin>
                                Add contact person
                            </P>
                        </ClickableOpacity>
                    </div>
                )}
                {lead?.address_details?.street_address ? (
                    <>
                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <div>
                                <div className={Style.shippingAddressTitle}>
                                    <div className={Style.shippingAddressLabel}>
                                        <P strong noDefaultMargin size="large">
                                            Shipping address
                                        </P>
                                        <span
                                            className={`${Style.shippingAddressPill} ${isAddressValidated ? Style.shippingAddressPillValidated : Style.shippingAddressPillUnValidated}`}
                                        >
                                            {isAddressValidated ? 'Validated' : 'Unvalidated'}
                                        </span>
                                    </div>

                                    {/* HWM-22633 (round 2): when validated, show the green
                                        pill; when not, show the orange Validate button in
                                        its place — inline next to the title per Figma. The
                                        button is gated only by lock state so it shows on
                                        the Activities tab even when hideEdit is true. */}
                                    {!isAddressValidated ? (
                                        <Button
                                            size="regular"
                                            onClick={handleValidateAddress}
                                            disabled={isValidating || isLocked}
                                            styles={{ lineHeight: '3.6rem' }}
                                        >
                                            {isValidating ? 'Validating…' : 'Validate'}
                                        </Button>
                                    ) : null}
                                </div>
                                {showEdit ? (
                                    <ClickableOpacity
                                        onClick={handleOpenShippingAddressForm}
                                        alt="Open shipping address form"
                                        className={Style.editBtn}
                                    >
                                        <P strong noDefaultMargin className={Style.editText}>
                                            Edit
                                        </P>
                                    </ClickableOpacity>
                                ) : null}
                            </div>
                            {addressErrors ? (
                                <div
                                    role="alert"
                                    aria-live="polite"
                                    className={Style.validationErrorBlock}
                                >
                                    <P strong noDefaultMargin size="small">
                                        Address is invalid:
                                    </P>
                                    <ul className={Style.validationErrorList}>
                                        {Object.entries(addressErrors).flatMap(([field, codes]) =>
                                            codes.map((code) => (
                                                <li key={`${field}-${code}`}>
                                                    {ADDRESS_FIELD_ERROR_TEXT[`${field}.${code}`] ??
                                                        `${field}: ${code}`}
                                                </li>
                                            ))
                                        )}
                                    </ul>
                                    <ClickableOpacity
                                        onClick={handleEditAddress}
                                        alt="Edit shipping address"
                                        className={Style.editBtn}
                                    >
                                        <P strong noDefaultMargin className={Style.editText}>
                                            Edit address →
                                        </P>
                                    </ClickableOpacity>
                                </div>
                            ) : null}
                            <P noDefaultMargin size="small" className={Style.label}>
                                Street address (no p.o. boxes)
                            </P>
                            <P noDefaultMargin styles={{ marginBottom: '1.2rem' }}>
                                {getLabel(lead?.address_details?.street_address)}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Apt, Suite, Etc
                            </P>
                            <P
                                styles={{
                                    marginBottom: '1.2rem'
                                }}
                            >
                                {getLabel(lead?.address_details?.apartment)}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                City
                            </P>
                            <P
                                styles={{
                                    marginBottom: '1.2rem'
                                }}
                            >
                                {getLabel(lead?.address_details?.city)}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                State
                            </P>
                            <P
                                styles={{
                                    marginBottom: '1.2rem'
                                }}
                            >
                                {getLabel(lead?.address_details?.state)}
                            </P>
                            <P noDefaultMargin size="small" className={Style.label}>
                                Zip
                            </P>
                            <P noDefaultMargin>{getLabel(lead?.address_details?.shipping_zip)}</P>
                        </Section>
                    </>
                ) : (
                    <div
                        className={Style.addWrapper}
                        style={{ ...(!showEdit && { display: 'none' }) }}
                    >
                        <Divider noDefaultMargin />
                        <ClickableOpacity
                            onClick={handleOpenShippingAddressForm}
                            alt="Open shipping address form"
                            className={Style.addFooter}
                            disabled={!showEdit}
                        >
                            <PlusIcon size="utility" width={20} height={20} type="brand" />
                            <P strong noDefaultMargin>
                                Add shipping information
                            </P>
                        </ClickableOpacity>
                    </div>
                )}

                {lead?.primary_user_details.signed_consent_uploaded ? (
                    <>
                        <Divider noDefaultMargin />
                        <Section padding="small">
                            <P size="large" strong>
                                Attachments
                            </P>
                            <ConsentPDF lead={lead} />
                        </Section>
                    </>
                ) : null}
            </Section>
            <Modal
                externalControls={[showPatientDetailsModal, setShowPatientDetailsModal]}
                isCancelable
                onClose={handleClosePatientDetailsForm}
                styles={{ padding: '0', width: '75rem' }}
            >
                <Section noDefaultPadding styles={{ width: '100%' }}>
                    <div
                        style={{
                            width: '100%',
                            textAlign: 'left',
                            marginTop: '0.4rem',
                            marginBottom: '3.6rem'
                        }}
                    >
                        <H role="h5" noDefaultMargin styles={{ fontSize: '24px' }}>
                            {`${lead?.primary_user_details.first_name} ${lead?.primary_user_details.last_name}`}
                        </H>
                        <P style={{ textAlign: 'left' }} noDefaultMargin>
                            Form fill ID: {lead?.primary_user_details?.lead_id} &#x2022; Created:
                            {''}
                            {lead?.primary_user_details.completed_date ||
                                lead?.primary_user_details.created_date}
                        </P>
                    </div>
                    <PatientDetailsForm lead={lead} onSuccess={handlePatientDetailsSuccess} />
                </Section>
            </Modal>
            <Modal
                externalControls={[showContactDetailsModal, setShowContactDetailsModal]}
                isCancelable
                onClose={handleCloseContactDetailsForm}
                styles={{ padding: '0', width: '75rem' }}
            >
                <Section noDefaultPadding styles={{ width: '100%' }}>
                    <ContactPersonForm lead={lead} onSuccess={handleContactDetailsSuccess} />
                </Section>
            </Modal>
            <Modal
                externalControls={[showShippingAddressModal, setShowShippingAddressModal]}
                isCancelable
                onClose={handleCloseShippingAddressForm}
                styles={{ padding: '0', width: '75rem' }}
            >
                <Section noDefaultPadding styles={{ width: '100%' }}>
                    <ShippingAddressForm lead={lead} onSuccess={handleShippingAddressSuccess} />
                </Section>
            </Modal>

            {/* HWM-22633: CRM card modals — only rendered when the action
                cards are visible (showCrmActions on the v2 Activities tab). */}
            {showCrmActions && lead && showCommunicationModal && (
                <CommunicationModal
                    leadId={effectiveLeadId}
                    patientName={patientName}
                    patientFirstName={lead.primary_user_details.first_name || undefined}
                    leadEmail={lead.primary_user_details.email || undefined}
                    messagingEnabled={messagingEnabled}
                    onClose={handleCloseCrmModal}
                />
            )}

            {showCrmActions && lead && showCallDispositionModal && (
                <CallDispositionModal
                    leadId={effectiveLeadId}
                    patientName={patientName}
                    onClose={handleCloseCrmModal}
                />
            )}

            {showCrmActions && lead && showPostponeSlaModal && (
                <PostponeSlaModal
                    leadId={effectiveLeadId}
                    patientName={patientName}
                    currentSlaText={slaStatus.text || undefined}
                    onClose={handleCloseCrmModal}
                />
            )}

            {/* HWM-22859: medical-check scheduling. The wrapper renders the
                manual date/time + note modal for non-DrChrono orgs and the
                DrChrono slot picker for Polycare-style orgs — this component
                doesn't branch on provider. Replaces the broken "reuse
                NewActivityForm" path that scheduled with no date/time. */}
            {showCrmActions && showMcModal && effectiveLeadId && (
                <MedicalCheckSchedulingModal
                    lead={lead}
                    leadId={effectiveLeadId}
                    isReschedule={isReschedulingMc}
                    onClose={handleCloseMcModal}
                    onSuccess={handleMcModalSuccess}
                />
            )}
        </>
    );
};

export default LeadContactDetails;
